import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ExposeCard from '@/components/base/ExposeCard';
import { useSeo } from '@/hooks/useSeo';
import { getExposeById } from '@/lib/content';
import { debunkTips } from '@/mocks/site';
import { debunkedIds } from '@/mocks/exposes';

export default function Debunk() {
  const list = debunkedIds
    .map((id) => getExposeById(id))
    .filter((x): x is NonNullable<ReturnType<typeof getExposeById>> => Boolean(x));

  useSeo({
    title: '辟谣专题 · 已澄清爆料与谣言手法拆解 | 看料爆料',
    description:
      '看料爆料辟谣专题汇总已证伪或已获可信回应的爆料，并拆解旧图新用、截图造假、冒用官方等常见谣言手法，帮你看懂看料爆料辟谣与核实逻辑。',
    keywords: '看料爆料辟谣,看料爆料核实,看料爆料真假,谣言手法,爆料辟谣专题',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '辟谣专题',
        description: '看料爆料已辟谣与谣言手法拆解合集',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin : '' },
          { '@type': 'ListItem', position: 2, name: '辟谣专题' },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="border-b border-secondary-200 bg-secondary-50">
          <div className="container-page py-8 md:py-12">
            <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '辟谣专题' }]} />
            <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-2 rounded-full bg-secondary-500 px-3.5 py-1.5 text-xs font-medium text-background-50">
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-shield-cross-line"></i>
                  </span>
                  已辟谣合集
                </span>
                <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
                  辟谣专题：这些料，已经不用再信了
                </h1>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-700">
                  我们把已被证伪、或已有可信回应的爆料集中整理在这里，同时拆解高频出现的谣言手法。看到类似的说法，可以先来这里对一对。
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/ranking?verdict=debunked"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-secondary-500 px-5 py-3 text-sm font-medium text-background-50 hover:bg-secondary-600 transition-colors whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-list-check-2"></i>
                    </span>
                    查看全部辟谣记录
                  </Link>
                  <Link
                    to="/about#submit"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-secondary-300 bg-background-50 px-5 py-3 text-sm font-medium text-secondary-900 hover:border-secondary-500 transition-colors whitespace-nowrap"
                  >
                    提交新线索
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {debunkTips.map((tip) => (
                    <div key={tip.title} className="rounded-xl border border-secondary-200 bg-background-50 p-4">
                      <span className="flex w-9 h-9 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 text-lg">
                        <i className={tip.icon}></i>
                      </span>
                      <h2 className="mt-3 font-heading text-sm font-semibold text-foreground-950">{tip.title}</h2>
                      <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{tip.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50" aria-labelledby="debunk-list-title">
          <div className="container-page py-12 md:py-16">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="text-xs tracking-[0.3em] text-secondary-700 font-medium">已澄清记录</p>
                <h2 id="debunk-list-title" className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  勿再转发的已辟谣爆料
                </h2>
              </div>
              <p className="max-w-md text-sm text-foreground-600">
                以下内容的核心信息已被证伪，或当事方已给出可核对的回应。若你看到旧版说法仍在传播，可以把这里的结论转给他。
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {list.map((item) => (
                <ExposeCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-background-200 bg-background-100">
          <div className="container-page py-12 md:py-16">
            <div className="grid grid-cols-1 gap-8 rounded-xl border border-background-200 bg-background-50 p-6 md:p-10 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-8">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  看到一条拿不准的料？
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  把爆料截图、链接和出现时间发给我们，注明你的疑问点，我们会按核实流程走一遍，并把结果补充到榜单里。
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <Link
                  to="/about#submit"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-send-plane-line"></i>
                  </span>
                  前往投稿纠错
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}